import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { concatMap, finalize } from 'rxjs';
import { StatementService } from '../../core/services/statement.service';
import { StatementType } from '../../core/models/statement-type';
import { StatementResponse } from '../../core/models/statement-response';

type UploadStatementForm = FormGroup<{
  type: FormControl<StatementType>;
  sourceName: FormControl<NonNullable<string>>;
}>;

@Component({
  selector: 'app-upload-statement',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './upload-statement.component.html',
  styleUrls: ['./upload-statement.component.css'],
})
export class UploadStatementComponent {
  form: UploadStatementForm = new FormGroup({
    type: new FormControl<StatementType>('BANK', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    sourceName: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(120)],
    }),
  });

  selectedFile: File | null = null;
  isSubmitting = false;
  errorMessage = '';
  createdStatement: StatementResponse | null = null;

  constructor(
    private statementService: StatementService,
    private router: Router,
  ) {}

  get type() {
    return this.form.controls.type;
  }

  get sourceName() {
    return this.form.controls.sourceName;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files && input.files.length > 0 ? input.files[0] : null;
  }

  onSubmit(): void {
    if (this.form.invalid || !this.selectedFile) {
      this.form.markAllAsTouched();
      this.errorMessage = !this.selectedFile ? 'Please select a file to upload.' : '';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.createdStatement = null;

    this.statementService.createStatement({
      type: this.type.value,
      sourceName: this.sourceName.value.trim(),
    }).pipe(
      concatMap(statement => {
        this.createdStatement = statement;
        return this.statementService.uploadStatementFile(statement.id, this.selectedFile!);
      }),
      finalize(() => {
        this.isSubmitting = false;
      })
    ).subscribe({
      next: statement => {
        this.router.navigate(['/app/statements', statement.id]);
      },
      error: error => {
        console.log('Statement upload error:', error);
        this.errorMessage =
          error?.error?.message ??
          error?.message ??
          'Statement upload failed. Please try again.';
      },
    });
  }
}
